import { fetchConfig } from '@/partials/el.Utils.js';

const RECOMMENDATIONS_API_URL = 'https://services.mybcapps.com/discovery/recommend';
const RECOMMENDATIONS_EXCLUDED_IDS = [];

const SELECTORS = {
  TRIGGER: '.js-cart-drawer-trigger',
  DRAWER_INNER: '#CartDrawer-Inner',
  QUANTITY_OPTION_BUTTON: '.el-cart-drawer__item-qty-selector--tooltip-option',
  REMOVE_BUTTON: '.js-cart-remove',
  RECOMMENDATIONS_WRAPPER: 'cart-drawer-recommendations',
  RECOMMENDATIONS_ITEMS_CONTAINER: '.el-cart-drawer-recommendations__items',
  RECOMMENDATIONS_ITEM_TEMPLATE: '.cart-drawer-recommendation-item',
};

const TEMPLATE_ID = 'el-cart-drawer';

const SECTIONS_TO_RENDER = [
  {
    id: 'el-cart-drawer-inner',
    selector: '#CartDrawer-Inner',
  },
  {
    id: 'cart-icon-bubble',
  },
];

elabs.CartDrawer = class {
  constructor() {
    if (elabs.CartDrawer.instance) {
      return elabs.CartDrawer.instance;
    }
    elabs.CartDrawer.instance = this;
    this.cachedDrawerHTML = null;
    this.recommendationsCache = null;
    this.recommendationsCacheKey = null;
    this.triggerBtn = document.querySelector(SELECTORS.TRIGGER);
    this._initialiseShowSideCart();
    if (this.triggerBtn) {
      this.init();
      this._subscribeToCartUpdates();
    }
  }

  _subscribeToCartUpdates() {
    document.addEventListener('cart:updated', (event) => {
      const { source, cart } = event.detail;
      if (source === 'cart-drawer') {
        return;
      }
      if (cart.sections['el-cart-drawer-inner']) {
        this.cachedDrawerHTML = cart.sections['el-cart-drawer-inner'];
      }
      this._renderSections(cart.sections);
      if (!document.body.classList.contains('overlay-menu-open')) {
        this.openOverlay();
      }
    });
  }

  init() {
    this.triggerBtn.addEventListener('click', (event) => {
      event.preventDefault();
      this.openOverlay();
    });
    document.addEventListener('overlay:closing:start', () => {
      if (this.drawer) {
        const recommendationsWrappers = this.drawer.querySelectorAll(SELECTORS.RECOMMENDATIONS_WRAPPER);
        recommendationsWrappers.forEach((wrapper) => wrapper.classList.remove('open'));
      }
    });
  }

  openOverlay = async () => {
    const overlayOptions = {};
    if (this.cachedDrawerHTML) {
      overlayOptions.htmlContent = this.cachedDrawerHTML;
    } else {
      overlayOptions.templateId = TEMPLATE_ID;
    }
    elabs.overlayMenuService.showMenu(overlayOptions, 'right', { wrapperClass: 'el-overlay-menu--cart' });
    setTimeout(this.addEventListeners, 50);
  };

  addEventListeners = () => {
    this.drawer = document.querySelector(SELECTORS.DRAWER_INNER);
    if (!this.drawer) {
      return;
    }
    this.drawer.addEventListener('click', (event) => {
      const quantityButton = event.target.closest(SELECTORS.QUANTITY_OPTION_BUTTON);
      const removeButton = event.target.closest(SELECTORS.REMOVE_BUTTON);
      if (quantityButton) {
        event.preventDefault();
        const cartItemElement = quantityButton.closest('.el-cart-drawer__item');
        if (!cartItemElement) {
          return;
        }
        const key = cartItemElement.dataset.lineItemKey;
        const newQuantity = quantityButton.dataset.value;
        this._updateQuantity(key, newQuantity);
        const tippyTrigger = cartItemElement.querySelector('[data-tippy-content]');
        if (tippyTrigger && tippyTrigger._tippy) {
          tippyTrigger._tippy.hide();
        }
      }
      if (removeButton) {
        event.preventDefault();
        const cartItemElement = removeButton.closest('.el-cart-drawer__item');
        if (!cartItemElement) {
          return;
        }
        const key = cartItemElement.dataset.lineItemKey;
        this._updateQuantity(key, 0);
      }
    });

    const recommendationsWrappers = this.drawer.querySelectorAll(SELECTORS.RECOMMENDATIONS_WRAPPER);
    if (recommendationsWrappers.length > 0) {
      setTimeout(() => {
        requestAnimationFrame(() => {
          recommendationsWrappers.forEach((wrapper) => wrapper.classList.add('open'));
        });
      }, 200);
    }
    this._initRecommendations();
  };

  _updateQuantity = (key, quantity) => {
    if (!key) {
      return;
    }
    this._toggleLoading(true, key);
    const body = JSON.stringify({
      id: key,
      quantity,
      sections: SECTIONS_TO_RENDER.map((section) => section.id),
      sections_url: window.location.pathname,
    });
    fetch(`${window.routes.cart_change_url}`, { ...fetchConfig(), body })
      .then((response) => response.json())
      .then((state) => {
        if (state.status === 422) {
          console.error(state.description);
          this._toggleLoading(false, key);
          return;
        }
        if (state.sections['el-cart-drawer-inner']) {
          this.cachedDrawerHTML = state.sections['el-cart-drawer-inner'];
        }
        this._renderSections(state.sections);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'cart-drawer', cart: state } }));
      })
      .catch((error) => console.error('Error updating cart:', error))
      .finally(() => this._toggleLoading(false, key));
  };

  _renderSections = (sections) => {
    if (!sections) {
      return;
    }
    SECTIONS_TO_RENDER.forEach((section) => {
      if (section.selector) {
        const elementToReplace = document.querySelector(section.selector);
        const newHtml = sections[section.id];
        if (elementToReplace && newHtml) {
          elementToReplace.innerHTML = this._getSectionInnerHTML(newHtml, section.selector);
        }
      }
    });
    if (this.drawer) {
      elabs.Bootstrap.initialiseTooltips(`${SELECTORS.DRAWER_INNER} [data-tippy-content]`);
    }

    const recommendationsWrappers = this.drawer?.querySelectorAll(SELECTORS.RECOMMENDATIONS_WRAPPER);
    recommendationsWrappers?.forEach((wrapper) => {
      wrapper.classList.add('no-transition');
      wrapper.classList.add('open');
      setTimeout(() => {
        wrapper.classList.remove('no-transition');
      }, 50);
    });

    this._initRecommendations();
  };

  _getSectionInnerHTML = (html, selector = 'body') =>
    new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML || '';

  _toggleLoading = (isLoading, key) => {
    if (!this.drawer) {
      return;
    }
    this.drawer.classList.toggle('loading', isLoading);
    if (!key) {
      return;
    }
    const loaderEl = this.drawer.querySelector(
      `.el-cart-drawer__item[data-line-item-key='${key}'] .el-cart-drawer__loader-spinner-wrapper`,
    );
    if (loaderEl) {
      loaderEl.classList.toggle('hidden', !isLoading);
    }
  };

  _initRecommendations = async () => {
    if (!this.drawer) {
      return;
    }
    const recommendationWrappers = this.drawer.querySelectorAll(SELECTORS.RECOMMENDATIONS_WRAPPER);
    if (recommendationWrappers.length === 0) {
      return;
    }

    // Clean up old cached recommendations (no longer used)
    this._cleanupOldRecommendationsCache();

    const productIds = JSON.parse(recommendationWrappers[0].dataset.productIds || '[]');
    const currencyIsoCode = recommendationWrappers[0].dataset.currencyIsoCode;
    const countryIsoCode = recommendationWrappers[0].dataset.countryIsoCode;
    const allIdsToExclude = [...productIds.map(String), ...RECOMMENDATIONS_EXCLUDED_IDS];
    const cacheKey = JSON.stringify([...productIds].sort());

    // Check if we have cached recommendations for these product IDs
    if (this.recommendationsCacheKey === cacheKey && this.recommendationsCache) {
      this._renderRecommendations(this.recommendationsCache, recommendationWrappers, allIdsToExclude);
      return;
    }

    const payload = {
      shop: window.Shopify.shop || 'sophiawebster.myshopify.com',
      recommendationType: 'frequently-bought-together',
      productIds,
      limit: 20,
      currency: currencyIsoCode?.toUpperCase() || 'GBP',
      currency_rate: 1,
      country: countryIsoCode || 'GB',
      return_all_currency_fields: false,
      ruleBased: [{ type: 'sameCollection', status: true, excluded: RECOMMENDATIONS_EXCLUDED_IDS }],
    };

    fetch(RECOMMENDATIONS_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(async (data) => {
        const { products } = data;
        // Cache minimal product data to save memory
        this.recommendationsCache = products.map((item) => this._getMinimalProductData(item));
        this.recommendationsCacheKey = cacheKey;
        this._renderRecommendations(this.recommendationsCache, recommendationWrappers, allIdsToExclude);
      })
      .catch((error) => console.error('Error with getting recommendations:', error));
  };

  /**
   * Extract only the fields we need for rendering to minimize memory usage
   */
  _getMinimalProductData = (item) => ({
    id: item.id,
    title: item.title,
    handle: item.handle,
    variants: item.variants.map((v) => ({ id: v.id })),
    images_info: item.images_info?.[0] ? [{ src: item.images_info[0].src, alt: item.images_info[0].alt }] : [],
    price_min: item.price_min,
    compare_at_price_min: item.compare_at_price_min,
  });

  /**
   * Render recommendations from cached or fresh data
   */
  _renderRecommendations = async (products, recommendationWrappers, allIdsToExclude) => {
    const filteredProducts = products.filter((item) => !allIdsToExclude.includes(String(item.id)));
    recommendationWrappers.forEach((wrapper) => {
      const container = wrapper.querySelector(SELECTORS.RECOMMENDATIONS_ITEMS_CONTAINER);
      if (container) {
        container.innerHTML = '';
      }
    });
    for (const item of filteredProducts) {
      for (const wrapper of recommendationWrappers) {
        const context = {
          template: wrapper.querySelector(SELECTORS.RECOMMENDATIONS_ITEM_TEMPLATE),
          container: wrapper.querySelector(SELECTORS.RECOMMENDATIONS_ITEMS_CONTAINER),
          currencySymbol: wrapper.dataset.currencySymbol,
          currencyIsoCode: wrapper.dataset.currencyIsoCode,
        };
        if (context.template && context.container) {
          await this._addRecommendationItem(item, context);
        }
      }
    }
  };

  /**
   * Remove old cached recommendations from localStorage
   */
  _cleanupOldRecommendationsCache = () => {
    try {
      localStorage.removeItem('cart-rec-items');
    } catch {
      // Ignore errors
    }
  };

  _addRecommendationItem = async (item, context) => {
    const { template, container, currencySymbol } = context;
    if (!template || !container) {
      return;
    }
    const compareAtPrice = item.compare_at_price_min;
    const price = item.price_min;
    const { images_info, title, handle, variants } = item;
    const image = images_info[0];
    const url = `/products/${handle}`;
    const productHasVariants = variants.length > 1;
    const clone = template.content.cloneNode(true);
    const mainLink = clone.querySelector('.js-product-link');
    mainLink.href = url;
    clone.querySelector('.js-text-wrapper').addEventListener('click', () => {
      window.location.href = url;
    });
    if (image) {
      const mainImage = clone.querySelector('.js-main-image');
      mainImage.src = image.src;
      mainImage.alt = image.alt || title;
      mainImage.classList.remove('hidden');
    } else {
      clone.querySelector('.js-placeholder-image').classList.remove('hidden');
    }
    if (productHasVariants) {
      clone.querySelectorAll('.js-recommendation-item-link').forEach((link) => {
        link.href = url;
        link.classList.remove('hidden');
      });
    } else {
      clone.querySelectorAll('.js-product-form').forEach((productForm) => {
        productForm.querySelector('form').id = `product_form_${item.id}`;
        productForm.querySelector('[name="id"]').value = variants[0].id;
        productForm.classList.remove('hidden');
      });
    }
    clone.querySelector('.js-title').textContent = title;
    if (productHasVariants) {
      const priceEl = clone.querySelector('.js-price-product-with-variants .el-price__price');
      priceEl.textContent = `${this._priceWithSymbol(price, currencySymbol)}`;
      if (price) {
        clone.querySelector('.js-price-product-with-variants').classList.remove('hidden');
      }
    } else {
      const priceComponent = clone.querySelector('.js-price-product-with-one-variant');
      if (price) {
        if (compareAtPrice) {
          const compareEl = priceComponent.querySelector('.el-price__compare-at-price');
          compareEl.querySelector('.compare_at_price').textContent = this._priceWithSymbol(
            compareAtPrice,
            currencySymbol,
          );
          compareEl.querySelector('.el-price__discounted-price').textContent = this._priceWithSymbol(
            price,
            currencySymbol,
          );
          compareEl.classList.remove('hidden');
        } else {
          const defaultPriceEl = priceComponent.querySelector('.el-price__price');
          defaultPriceEl.textContent = this._priceWithSymbol(price, currencySymbol);
          defaultPriceEl.classList.remove('hidden');
        }
        priceComponent.classList.remove('hidden');
      }
    }
    container.appendChild(clone);
  };
  _priceWithSymbol = (value, currencySymbol) => `${currencySymbol}${value}`;

  // When someone visits /cart it redirects to /?show-cart=true and then here we show the side cart and tidy up the URL
  _initialiseShowSideCart = () => {
    if (window.location.search.includes('show-cart=true')) {
      const newUrl = window.location.href.replace('?show-cart=true', '');
      history.replaceState(null, null, newUrl);
      setTimeout(() => {
        this.openOverlay();
      }, 500);
    }
  };
};

elabs.onJavascriptReady(() => {
  elabs.cartDrawer = new elabs.CartDrawer();
});
